import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { TEMA } from '../theme';
import InstallningarFlikar from '../components/InstallningarFlikar';

const kortStyle: React.CSSProperties = {
  background: TEMA.bgPanel,
  border: `1px solid ${TEMA.border}`,
  borderRadius: '8px',
  padding: '22px 24px',
  marginBottom: '16px',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '9px 12px',
  fontSize: '14px',
  fontFamily: "'Inter', sans-serif",
  borderRadius: '2px',
  border: `1px solid ${TEMA.border}`,
  background: TEMA.bg,
  color: TEMA.ink,
  boxSizing: 'border-box',
  outline: 'none',
};

const labelStyle: React.CSSProperties = {
  fontFamily: "'IBM Plex Mono', monospace",
  fontSize: '11px',
  color: TEMA.inkFaint,
  display: 'block',
  marginBottom: '6px',
};

const btnPrimärStyle: React.CSSProperties = {
  padding: '9px 20px',
  borderRadius: '2px',
  background: TEMA.accent,
  color: TEMA.onAccent,
  fontFamily: "'Inter', sans-serif",
  fontSize: '13px',
  fontWeight: 500,
  border: `1px solid ${TEMA.accent}`,
  cursor: 'pointer',
};

export default function Profil() {
  const { profil, supabase, användare } = useAuth();
  const [nyttNamn, setNyttNamn] = useState('');
  const [nyttLösenord, setNyttLösenord] = useState('');
  const [bekräftaLösenord, setBekräftaLösenord] = useState('');
  const [meddelande, setMeddelande] = useState('');
  const [fel, setFel] = useState('');
  const [sparar, setSparar] = useState(false);
  const [profilbildUrl, setProfilbildUrl] = useState<string | null>(null);
  const [laddaUppBild, setLaddaUppBild] = useState(false);
  const [sparatFält, setSparatFält] = useState<string | null>(null);
  const [utskick, setUtskick] = useState<{ daglig_sammanfattning: boolean; eftermiddag_paminnelse: boolean } | null>(null);

  useEffect(() => {
    if (profil) setNyttNamn(profil.namn);

    async function laddaBild() {
      if (!användare) return;
      const { data } = supabase.storage
        .from('profilbilder')
        .getPublicUrl(`${användare.id}/avatar.jpg`);
      if (data?.publicUrl) {
        const res = await fetch(data.publicUrl, { method: 'HEAD' });
        if (res.ok) setProfilbildUrl(data.publicUrl + '?t=' + Date.now());
      }
    }

    laddaBild();
  }, [profil, användare, supabase]);

  useEffect(() => {
    if (!användare) return;
    supabase
      .from('profiles')
      .select('daglig_sammanfattning, eftermiddag_paminnelse')
      .eq('id', användare.id)
      .single()
      .then(({ data }: { data: { daglig_sammanfattning?: boolean; eftermiddag_paminnelse?: boolean } | null }) => {
        if (data) setUtskick({
          daglig_sammanfattning: data.daglig_sammanfattning !== false,
          eftermiddag_paminnelse: data.eftermiddag_paminnelse !== false,
        });
      });
  }, [användare, supabase]);

  // Sparas direkt. Går det inte återställs rutan, så att den visar det som faktiskt gäller.
  async function växlaUtskick(fält: 'daglig_sammanfattning' | 'eftermiddag_paminnelse') {
    if (!utskick || !användare) return;
    const nytt = !utskick[fält];
    setUtskick({ ...utskick, [fält]: nytt });
    setMeddelande('');
    setFel('');
    const { error } = await supabase.from('profiles').update({ [fält]: nytt }).eq('id', användare.id);
    if (error) {
      setUtskick({ ...utskick, [fält]: !nytt });
      setFel('Kunde inte spara inställningen.');
    } else {
      setSparatFält(fält);
      setTimeout(() => setSparatFält(f => (f === fält ? null : f)), 2000);
    }
  }

  async function sparaNamn(e: React.FormEvent) {
    e.preventDefault();
    setSparar(true);
    setMeddelande('');
    setFel('');
    const { error } = await supabase
      .from('profiles')
      .update({ namn: nyttNamn })
      .eq('id', användare.id);
    if (error) setFel('Kunde inte spara namn.');
    else setMeddelande('Namnet uppdaterades!');
    setSparar(false);
  }

  async function bytaLösenord(e: React.FormEvent) {
    e.preventDefault();
    setMeddelande('');
    setFel('');
    if (nyttLösenord !== bekräftaLösenord) {
      setFel('Lösenorden matchar inte.');
      return;
    }
    if (nyttLösenord.length < 6) {
      setFel('Lösenordet måste vara minst 6 tecken.');
      return;
    }
    setSparar(true);
    const { error } = await supabase.auth.updateUser({ password: nyttLösenord });
    if (error) setFel('Kunde inte byta lösenord: ' + error.message);
    else {
      setMeddelande('Lösenordet byttes!');
      setNyttLösenord('');
      setBekräftaLösenord('');
    }
    setSparar(false);
  }

  async function hanteraProfilbild(e: React.ChangeEvent<HTMLInputElement>) {
    const fil = e.target.files?.[0];
    if (!fil || !användare) return;
    setLaddaUppBild(true);
    setFel('');
    const { error } = await supabase.storage
      .from('profilbilder')
      .upload(`${användare.id}/avatar.jpg`, fil, { upsert: true, contentType: fil.type });
    if (error) {
      setFel('Kunde inte ladda upp bild: ' + error.message);
    } else {
      setMeddelande('Profilbilden uppdaterades!');
      const { data } = supabase.storage
        .from('profilbilder')
        .getPublicUrl(`${användare.id}/avatar.jpg`);
      if (data?.publicUrl) setProfilbildUrl(data.publicUrl + '?t=' + Date.now());
    }
    setLaddaUppBild(false);
  }

  return (
    <div>
      <h1 style={{ fontFamily: "'Fraunces', serif", fontSize: '26px', color: TEMA.ink, marginBottom: '24px' }}>Inställningar</h1>
      <InstallningarFlikar />

      {meddelande && <p style={{ color: TEMA.positive, fontSize: '13px', marginBottom: '16px', fontFamily: "'Inter', sans-serif" }}>{meddelande}</p>}
      {fel && <p style={{ color: '#D9695F', fontSize: '13px', marginBottom: '16px', fontFamily: "'Inter', sans-serif" }}>{fel}</p>}

      <div style={kortStyle}>
        <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: '17px', color: TEMA.ink, margin: '0 0 18px' }}>Profilbild</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
          <div style={{
            width: '72px', height: '72px', borderRadius: '50%',
            background: 'rgba(224,168,62,0.12)', display: 'flex', alignItems: 'center',
            justifyContent: 'center', fontFamily: "'Fraunces', serif", fontSize: '24px', fontWeight: 500,
            color: TEMA.accent, overflow: 'hidden', flexShrink: 0,
          }}>
            {profilbildUrl
              ? <img src={profilbildUrl} alt="Profilbild" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : profil?.namn?.charAt(0).toUpperCase()
            }
          </div>
          <div>
            <label style={{
              padding: '9px 18px', borderRadius: '2px', background: TEMA.accent,
              color: TEMA.onAccent, fontFamily: "'Inter', sans-serif", fontSize: '13px', fontWeight: 500,
              border: `1px solid ${TEMA.accent}`, cursor: 'pointer', display: 'inline-block',
            }}>
              {laddaUppBild ? 'Laddar upp...' : 'Välj bild'}
              <input type="file" accept="image/*" onChange={hanteraProfilbild} style={{ display: 'none' }} />
            </label>
            <p style={{ fontSize: '12px', color: TEMA.inkFaint, margin: '8px 0 0', fontFamily: "'Inter', sans-serif" }}>JPG, PNG eller GIF. Max 2MB.</p>
          </div>
        </div>
      </div>

      <div style={kortStyle}>
        <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: '17px', color: TEMA.ink, margin: '0 0 18px' }}>Namn</h2>
        <form onSubmit={sparaNamn}>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Visningsnamn</label>
            <input style={inputStyle} value={nyttNamn} onChange={e => setNyttNamn(e.target.value)} required />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>E-post</label>
            <input style={{ ...inputStyle, color: TEMA.inkFaint }} value={användare?.email || ''} disabled />
          </div>
          <button type="submit" disabled={sparar} style={{ ...btnPrimärStyle, opacity: sparar ? 0.6 : 1 }}>
            {sparar ? 'Sparar...' : 'Spara namn'}
          </button>
        </form>
      </div>

      <div style={kortStyle}>
        <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: '17px', color: TEMA.ink, margin: '0 0 8px' }}>Mejlutskick</h2>
        <p style={{ fontSize: '13px', color: TEMA.inkSoft, margin: '0 0 12px', fontFamily: "'Inter', sans-serif", lineHeight: 1.5 }}>
          Välj vilka automatiska mejl du vill få. Ändringen sparas direkt.
        </p>
        {!utskick ? (
          <p style={{ fontSize: '13px', color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif" }}>Laddar...</p>
        ) : (
          [
            { fält: 'daglig_sammanfattning' as const, rubrik: 'Morgonmejl', text: 'Vardagar kl. 07: dagens Att göra.' },
            { fält: 'eftermiddag_paminnelse' as const, rubrik: 'Eftermiddagspåminnelse', text: 'Vardagar kl. 15: det som väntar under Att åtgärda. Skickas bara när något väntar.' },
          ].map(u => (
            <label key={u.fält} style={{
              display: 'flex', gap: '12px', alignItems: 'flex-start', padding: '10px 0',
              cursor: 'pointer', fontFamily: "'Inter', sans-serif",
            }}>
              <input
                type="checkbox"
                checked={utskick[u.fält]}
                onChange={() => växlaUtskick(u.fält)}
                style={{ marginTop: '3px', width: '16px', height: '16px', accentColor: TEMA.accent, cursor: 'pointer' }}
              />
              <span>
                <span style={{ display: 'block', fontSize: '14px', color: TEMA.ink }}>
                  {u.rubrik}
                  {sparatFält === u.fält && (
                    <span style={{ marginLeft: '10px', fontSize: '12px', color: TEMA.positive }}>Sparat</span>
                  )}
                </span>
                <span style={{ display: 'block', fontSize: '12.5px', color: TEMA.inkFaint, marginTop: '2px' }}>{u.text}</span>
              </span>
            </label>
          ))
        )}
      </div>

      <div style={kortStyle}>
        <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: '17px', color: TEMA.ink, margin: '0 0 18px' }}>Byta lösenord</h2>
        <form onSubmit={bytaLösenord}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '16px' }}>
            <div>
              <label style={labelStyle}>Nytt lösenord</label>
              <input type="password" style={inputStyle} value={nyttLösenord}
                onChange={e => setNyttLösenord(e.target.value)} placeholder="Minst 6 tecken" required />
            </div>
            <div>
              <label style={labelStyle}>Bekräfta lösenord</label>
              <input type="password" style={inputStyle} value={bekräftaLösenord}
                onChange={e => setBekräftaLösenord(e.target.value)} placeholder="Upprepa lösenord" required />
            </div>
          </div>
          <button type="submit" disabled={sparar} style={{ ...btnPrimärStyle, opacity: sparar ? 0.6 : 1 }}>
            {sparar ? 'Sparar...' : 'Byt lösenord'}
          </button>
        </form>
      </div>
    </div>
  );
}